import React, { useRef, useState, ChangeEvent } from 'react'
import type { BaseSlice } from './stores/baseReportStore'
import {
  WrapHStack,
  ValueInfoBigStyle,
  ValueInfoNormalStyle,
  TitleContainer,
  HeaderContainer,
  ValueSymbolContainer,
  HeaderTop,
  LogoContainer,
  ValueInfoMediumStyle,
  ModelName,
} from './styles/ReportHeader.styles'
import HeaderInfoDisplayer from '../../../ui_reusable/HeaderInfoDisplayer'
import { QualityBall } from '../../../comp_reusable/model/QualityTag'
import CustomIcons from '../../../ui_reusable/CustomIcons'
import { HeaderInput } from '../../../ui_reusable/StandardInputs'
import LoadingText from '../../../ui_reusable/LoadingText'
import { getHealthStringByScore } from '../../../../utils/ScoreFitnessValidation'
import { capitalize } from '../../../../utils/helper'
import type { AnyReport } from './stores/baseReportStore'

// Quality type from QualityTag component
type Quality = 'good' | 'warning' | 'bad'

// Type for a store that implements BaseSlice
type BaseStoreHook<T extends BaseSlice> = (selector: (state: T) => any) => any

interface ReportHeaderProps {
  data?: AnyReport | null
  store?: BaseStoreHook<any>
}

// Helper function to convert score function results to Quality type
const mapToQuality = (qualityString: string): Quality => {
  switch (qualityString.toLowerCase()) {
    case 'good':
      return 'good'
    case 'moderate':
    case 'check':
      return 'warning'
    case 'low':
    case 'fix':
    case 'bad':
      return 'bad'
    default:
      return 'warning'
  }
}

// Helper function to safely convert string/number to number
const toNumber = (value: string | number | undefined | null): number => {
  if (typeof value === 'number') return value
  if (typeof value === 'string') return parseFloat(value) || 0
  return 0
}

const ReportHeader: React.FC<ReportHeaderProps> = ({ data, store }) => {
  // Get title and setter from store - use passed store or fallback to null
  const storeTitle = store
    ? store((state: BaseSlice) => state.getTitle())
    : null
  const setTitle = store
    ? store((state: BaseSlice) => state.setTitle)
    : () => {}

  // Use store title only
  const displayTitle = storeTitle

  const inputRef = useRef<HTMLInputElement>(null)
  const [localTitle, setLocalTitle] = useState<string>(displayTitle || '')
  const [isFocused, setIsFocused] = useState<boolean>(false)

  const handleFocusTitle = (): void => {
    inputRef.current?.focus()
  }

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value
    setLocalTitle(newValue)
  }

  const handleTitleFocus = (): void => {
    setIsFocused(true)
  }

  const handleTitleBlur = (): void => {
    setIsFocused(false)
    setTitle(localTitle)
  }

  React.useEffect(() => {
    // Only update local title from store if input is not focused
    if (!isFocused) {
      setLocalTitle(displayTitle || '')
    }
  }, [displayTitle, isFocused])

  // Helper function to safely format dates
  const formatDate = (dateString?: string): string => {
    if (!dateString) return ''
    return new Date(dateString).toISOString().slice(0, 10)
  }

  // Helper function to get timeframe display
  const getTimeframeDisplay = (): string => {
    if (!data?.timeframe) return ''
    return `${formatDate(data.timeframe.from)} - ${formatDate(data.timeframe.to)}`
  }

  const getOptimizationTimeframeDisplay = (): string => {
    // @ts-ignore
    if (data?.artifact?.optimize_config?.timeframe) {
      // @ts-ignore
      return `${formatDate(data.artifact.optimize_config.timeframe.from)} - ${formatDate(data.artifact.optimize_config.timeframe.to)}`
    }
    return ''
  }

  // Helper function to get drivers display
  const getDriversDisplay = (): string => {
    if (!data?.no_of_drivers) return ''

    if (data.solution === 'IMPACT') {
      // @ts-ignore
      const { own, external } = data.no_of_drivers
      return `${own} internal + ${external} external`
    } else if (data.solution === 'OPTIMIZE') {
      // @ts-ignore
      const { channels, control } = data.no_of_drivers
      return `${channels} channels + ${control} control`
    }
    return ''
  }

  // Helper function to get granularity display
  const getGranularityDisplay = (): string => {
    if (!data?.model_data_frequency || !data?.total_observations) return ''
    return capitalize(data.model_data_frequency)
  }

  // Helper function to get logo URL
  const getLogoUrl = (): string | null => {
    const projectLogo = data?.artifact?.project_solution?.project?.logo
    const clientLogo = data?.artifact?.project_solution?.project?.client?.logo
    const logo = projectLogo || clientLogo

    if (!logo) return null

    return logo
  }

  return (
    <HeaderContainer>
      <ModelName>
        {data?.solution === 'OPTIMIZE' ? 'Optimize MMM' : 'Impact Model'}
      </ModelName>
      <HeaderTop>
        {storeTitle && (
          <TitleContainer>
            <HeaderInput
              ref={inputRef}
              value={localTitle || ''}
              onChange={handleTitleChange}
              onFocus={handleTitleFocus}
              onBlur={handleTitleBlur}
              style={{
                width: `${Math.max((localTitle || '').length * 1.54, 0)}rem`,
                maxWidth: '60rem',
              }}
            />
            <CustomIcons.PenIcon size="small" onClick={handleFocusTitle} />
          </TitleContainer>
        )}
        {getLogoUrl() && (
          <LogoContainer>
            <img src={getLogoUrl()!} alt="Project logo" />
          </LogoContainer>
        )}
      </HeaderTop>

      <WrapHStack>
        <HeaderInfoDisplayer title="Run date" symbol={null}>
          <LoadingText css={ValueInfoNormalStyle} isLoading={!data}>
            {formatDate(data?.run_at)}
          </LoadingText>
        </HeaderInfoDisplayer>

        <HeaderInfoDisplayer title="Outcome variable" symbol={null}>
          <LoadingText css={ValueInfoMediumStyle} isLoading={!data}>
            {data?.outcome_variable}
          </LoadingText>
        </HeaderInfoDisplayer>

        <HeaderInfoDisplayer title="Model fit score">
          <LoadingText css={ValueInfoBigStyle} isLoading={!data}>
            <ValueSymbolContainer>
              {data?.model_fit_score
                ? Math.round(toNumber(data.model_fit_score))
                : 0}
            </ValueSymbolContainer>
          </LoadingText>
        </HeaderInfoDisplayer>

        <HeaderInfoDisplayer title="Model health score">
          <LoadingText css={ValueInfoBigStyle} isLoading={!data}>
            <ValueSymbolContainer>
              <span>
                {data?.model_health_score
                  ? Math.round(toNumber(data.model_health_score))
                  : 0}
              </span>
              <QualityBall
                size="s"
                quality={mapToQuality(
                  getHealthStringByScore(
                    toNumber(data?.model_health_score) * 10,
                  ),
                )}
              />
            </ValueSymbolContainer>
          </LoadingText>
        </HeaderInfoDisplayer>

        <HeaderInfoDisplayer
          title={
            data?.solution === 'OPTIMIZE' ? 'Modeling Period' : 'Timeframe'
          }
          symbol={null}
        >
          <LoadingText css={ValueInfoNormalStyle} isLoading={!data}>
            {getTimeframeDisplay()}
          </LoadingText>
        </HeaderInfoDisplayer>

        {data?.solution === 'OPTIMIZE' ? (
          <HeaderInfoDisplayer title="Optimization period" symbol={null}>
            <LoadingText css={ValueInfoNormalStyle} isLoading={!data}>
              {getOptimizationTimeframeDisplay()}
            </LoadingText>
          </HeaderInfoDisplayer>
        ) : (
          <HeaderInfoDisplayer title="Observations in timeframe" symbol={null}>
            <LoadingText css={ValueInfoNormalStyle} isLoading={!data}>
              {toNumber(data?.total_observations)}
            </LoadingText>
          </HeaderInfoDisplayer>
        )}

        <HeaderInfoDisplayer title="Granularity" symbol={null}>
          <LoadingText css={ValueInfoNormalStyle} isLoading={!data}>
            {getGranularityDisplay()}
          </LoadingText>
        </HeaderInfoDisplayer>

        <HeaderInfoDisplayer title="Number of drivers" symbol={null}>
          <LoadingText css={ValueInfoNormalStyle} isLoading={!data}>
            {getDriversDisplay()}
          </LoadingText>
        </HeaderInfoDisplayer>
      </WrapHStack>
    </HeaderContainer>
  )
}

export default ReportHeader
